'use client';
import React, { useState } from 'react';
import { X, Share2, Copy, Mail, MessageSquare, CheckCircle, Lock } from 'lucide-react';

type ShareState = 'idle' | 'copied' | 'error';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  url?: string;
  contentType?: 'profile' | 'conversation' | 'listing' | 'service' | 'event' | 'newsletter' | 'resource';
}

export default function ShareModal({
  isOpen,
  onClose,
  title,
  url,
  contentType = 'profile',
}: ShareModalProps) {
  const [copyState, setCopyState] = useState<ShareState>('idle');

  if (!isOpen) return null;

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const displayUrl = shareUrl.replace(/^https?:\/\//, '').slice(0, 48) + (shareUrl.length > 48 ? '…' : '');

  function handleCopy() {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          setCopyState('copied');
          setTimeout(() => setCopyState('idle'), 2500);
        })
        .catch(() => setCopyState('error'));
    } else {
      setCopyState('error');
    }
  }

  function handleEmail() {
    const subject = encodeURIComponent(`Panorama: ${title || 'Community content'}`);
    const body = encodeURIComponent(`I thought you might find this interesting:\n\n${shareUrl}\n\nNote: This is private Panorama community content. Access requires verification.`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  }

  function handleText() {
    const body = encodeURIComponent(`Panorama community: ${shareUrl}`);
    window.open(`sms:?body=${body}`);
  }

  const contentLabel = {
    profile: 'resident profile',
    conversation: 'conversation',
    listing: 'marketplace listing',
    service: 'service listing',
    event: 'event',
    newsletter: 'newsletter issue',
    resource: 'resource',
  }[contentType];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ background: 'rgba(7,6,12,0.80)' }}
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-sm rounded-2xl shadow-2xl"
        style={{
          background: '#0C0A16',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.60)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-5 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(76,201,255,0.08)', border: '1px solid rgba(76,201,255,0.15)' }}
            >
              <Share2 size={14} style={{ color: '#4CC9FF' }} />
            </div>
            <div>
              <h2 className="font-display font-semibold text-[15px]" style={{ color: '#F2F4FF' }}>
                Share {contentLabel}
              </h2>
              {title && (
                <p className="text-[12px] truncate max-w-[180px]" style={{ color: '#A7A3C4' }}>{title}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-all duration-150 hover:bg-white/[0.06] active:scale-95"
            style={{ color: '#A7A3C4' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Privacy notice */}
          <div
            className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl mb-5"
            style={{ background: 'rgba(76,201,255,0.04)', border: '1px solid rgba(76,201,255,0.10)' }}
          >
            <Lock size={13} className="flex-shrink-0 mt-0.5" style={{ color: '#4CC9FF' }} />
            <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.70)' }}>
              This is private community content. Recipients will need verified Panorama access to view it.
            </p>
          </div>

          {/* URL display */}
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-xl mb-4"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <span className="text-[12.5px] flex-1 truncate font-mono" style={{ color: 'rgba(167,163,196,0.60)' }}>
              {displayUrl}
            </span>
          </div>

          {/* Share actions */}
          <div className="space-y-2.5 mb-5">
            <button
              onClick={handleCopy}
              className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl transition-all duration-150 hover:bg-white/[0.04] active:scale-[0.98]"
              style={{
                background: copyState === 'copied' ? 'rgba(45,212,160,0.06)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${copyState === 'copied' ? 'rgba(45,212,160,0.20)' : 'rgba(255,255,255,0.07)'}`,
              }}
            >
              {copyState === 'copied' ? (
                <CheckCircle size={18} style={{ color: '#2DD4A0' }} />
              ) : (
                <Copy size={18} style={{ color: '#4CC9FF' }} />
              )}
              <span className="text-[14px] font-medium" style={{ color: copyState === 'copied' ? '#2DD4A0' : '#F2F4FF' }}>
                {copyState === 'copied' ? 'Link copied!' : copyState === 'error' ? 'Copy failed — try again' : 'Copy link'}
              </span>
            </button>

            <button
              onClick={handleEmail}
              className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl transition-all duration-150 hover:bg-white/[0.04] active:scale-[0.98]"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <Mail size={18} style={{ color: '#9B7FFF' }} />
              <span className="text-[14px] font-medium" style={{ color: '#F2F4FF' }}>Share via email</span>
            </button>

            <button
              onClick={handleText}
              className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl transition-all duration-150 hover:bg-white/[0.04] active:scale-[0.98]"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <MessageSquare size={18} style={{ color: '#F0A832' }} />
              <span className="text-[14px] font-medium" style={{ color: '#F2F4FF' }}>Share via text</span>
            </button>
          </div>

          <button onClick={onClose} className="btn-secondary w-full py-2.5 text-[13.5px]">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
